import React, { useState, useEffect } from 'react';
import { subDays, addDays } from 'date-fns';
import AnalyticsHeader from './AnalyticsHeader';
import AnalyticsDashboard from './AnalyticsDashboard';
import DatePickerModal from './DatePickerModal';
import { useRealTimeData } from '../hooks/useRealTimeData';

const AnalyticsPage = () => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedRangeType, setSelectedRangeType] = useState(30); // Store active quick range: 7, 14, 30, 90, or 'custom'
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 30),
    end: new Date()
  });

  const [isSimulatingLoading, setIsSimulatingLoading] = useState(true);

  useEffect(() => {
    setIsSimulatingLoading(true);
    const timer = setTimeout(() => {
      setIsSimulatingLoading(false);
    }, 500); // 500ms delay to feel like it's fetching data
    return () => clearTimeout(timer);
  }, [dateRange]);

  const data = useRealTimeData(dateRange);

  const handleReset = () => {
    setSelectedRangeType(30);
    setDateRange({
      start: subDays(new Date(), 30),
      end: new Date()
    });
  };

  const handleQuickSelect = (days) => {
    setSelectedRangeType(days);
    setDateRange({
      start: subDays(new Date(), days),
      end: new Date()
    });
  };

  const handleShiftDate = (direction) => {
    // direction: -1 (back), 1 (forward)
    // Shift by selectedRangeType days, unless it's custom. If custom, shift by difference between start and end.
    let daysToShift = selectedRangeType;
    if (daysToShift === 'custom') {
      daysToShift = Math.round((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 3600 * 24)) || 1;
    }
    
    setDateRange(prev => ({
      start: direction === -1 ? subDays(prev.start, daysToShift) : addDays(prev.start, daysToShift),
      end: direction === -1 ? subDays(prev.end, daysToShift) : addDays(prev.end, daysToShift)
    }));
  };

  const handleApplyRange = (newRange) => {
    setSelectedRangeType('custom');
    setDateRange(newRange);
    setIsDatePickerOpen(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#F8FAFC]">
      <AnalyticsHeader 
        onDateRangeClick={() => setIsDatePickerOpen(true)} 
        onReset={handleReset}
        dateRange={dateRange}
        onQuickSelect={handleQuickSelect}
        onShiftDate={handleShiftDate}
        selectedRangeType={selectedRangeType}
      />
      <div className="flex-1 relative">
        {isSimulatingLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#F8FAFC] z-10" style={{ minHeight: '600px' }}>
             <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
             <p className="text-gray-500 font-medium">Crunching analytics...</p>
          </div>
        ) : (
          <AnalyticsDashboard data={data} key={`${dateRange.start.getTime()}-${dateRange.end.getTime()}`} />
        )}
      </div>
      
      {isDatePickerOpen && (
        <DatePickerModal 
          initialRange={dateRange}
          onClose={() => setIsDatePickerOpen(false)} 
          onApply={handleApplyRange}
        />
      )}
    </div>
  );
};

export default AnalyticsPage;
